import React, { useEffect, useMemo, useState } from 'react';

import { EnvironmentOutlined } from '@ant-design/icons';
import {
  App,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  AdminCityRow,
  AdminUserDetail,
  LeadPaymentDetail,
  PatchProfilePaymentResponse,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

import {
  PaymentShippingValues,
  buildPaymentPatch,
  getPaymentErrorField,
  getPaymentShippingValues,
} from 'utils/LeadPaymentHelper';

import appI18n from '../../../../config/i18n';
import { getCityLabelKey, getRegionLabelKey } from '../leadHelpers';
import SectionActions from './SectionActions';
import SectionEditFooter from './SectionEditFooter';

const KEY =
  'scenes.leads.components.detail_lead_modal.sections.payment_shipping';
const SHARED = 'scenes.leads.components.detail_lead_modal.sections.payment';
const BASIC = 'scenes.leads.components.detail_lead_modal.sections.basic_info';

interface Props {
  detail: AdminUserDetail;
  payment: LeadPaymentDetail;
  cities: AdminCityRow[];
  citiesLoading: boolean;
  onUpdated: (next: AdminUserDetail) => void;
}

function PaymentShippingSection({
  detail,
  payment,
  cities,
  citiesLoading,
  onUpdated,
}: Props) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const sameAsResidence = Form.useWatch('shippingSameAsResidence', form);
  const regionId = Form.useWatch('shippingRegionId', form);

  useEffect(() => {
    form.setFieldsValue(getPaymentShippingValues(payment));
  }, [payment, form]);

  const countryCode = detail.user.countryCode;
  const regionLabel = t(`${BASIC}.labels.${getRegionLabelKey(countryCode)}`);
  const cityLabel = t(`${BASIC}.labels.${getCityLabelKey(countryCode)}`);

  const regionOptions = useMemo(() => {
    const map = new Map<string, string>();
    cities.forEach((c) => {
      if (!map.has(c.regionId)) map.set(c.regionId, c.regionName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));
  }, [cities]);

  const cityOptions = useMemo(
    () =>
      regionId
        ? cities
            .filter((c) => c.regionId === regionId)
            .map((c) => ({ value: c.id, label: c.cityName }))
        : [],
    [cities, regionId]
  );

  const residence = [detail.profile.address, detail.city?.cityName]
    .filter(Boolean)
    .join(', ');

  // Street, number and comuna count only for leads living outside the RM,
  // and only when they do not use their residence address.
  const shippingRequired =
    editing && payment.shipping.required && !sameAsResidence;
  const required = (key: string) => ({
    required: shippingRequired,
    message: t(`${KEY}.validations.${key}_required`),
  });

  const handleCancel = () => {
    if (saving) return;
    form.resetFields();
    form.setFieldsValue(getPaymentShippingValues(payment));
    setEditing(false);
  };

  const handleSave = async () => {
    let values: PaymentShippingValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Hidden fields keep their stored values while the residence is used.
    const all = { ...form.getFieldsValue(true), ...values };
    const patch = buildPaymentPatch(getPaymentShippingValues(payment), all);
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }

    setSaving(true);
    const response = await LeadService.updateProfilePayment(
      detail.user.id,
      patch
    );
    setSaving(false);

    if (response.success) {
      message.success(t(`${SHARED}.messages.update_success`));
      onUpdated({
        ...detail,
        payment: (response.data as PatchProfilePaymentResponse).updatedProfile,
      });
      setEditing(false);
      return;
    }

    const error = response.data as ApiErrorData;
    message.error(
      (error.message as string) || t(`${SHARED}.messages.update_failed`),
      3.5
    );
    if (getPaymentErrorField(error.code) === 'shippingCityId') {
      form.setFields([
        {
          name: 'shippingCityId',
          value: undefined,
          errors: [t(`${KEY}.validations.city_unavailable`)],
        },
      ]);
    }
  };

  const fieldsDisabled = !editing || saving;

  return (
    <Card
      className={
        editing
          ? 'lead-section-card lead-section-card--editing'
          : 'lead-section-card'
      }
      size="small"
      title={
        <Space size={8}>
          <EnvironmentOutlined />
          <span>{t(`${KEY}.title`)}</span>
        </Space>
      }
      extra={
        <SectionActions editing={editing} onEdit={() => setEditing(true)} />
      }
    >
      <Form form={form} layout="vertical" disabled={!editing}>
        <Form.Item name="shippingSameAsResidence" valuePropName="checked">
          <Checkbox disabled={fieldsDisabled}>
            {t(`${KEY}.labels.same_as_residence`)}
            {residence && (
              <Typography.Text type="secondary"> ({residence})</Typography.Text>
            )}
          </Checkbox>
        </Form.Item>

        {/* Kept mounted while hidden so the stored values are not lost. The
            `hidden` attribute alone loses against `.ant-row { display: flex }`. */}
        <Row
          gutter={16}
          style={sameAsResidence ? { display: 'none' } : undefined}
        >
          <Col xs={24} md={16}>
            <Form.Item
              name="shippingStreet"
              label={t(`${KEY}.labels.street`)}
              rules={[required('street')]}
            >
              <Input
                maxLength={200}
                placeholder={
                  editing ? t(`${KEY}.placeholders.street`) : undefined
                }
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="shippingNumber"
              label={t(`${KEY}.labels.number`)}
              rules={[required('number')]}
            >
              <Input
                maxLength={20}
                placeholder={
                  editing ? t(`${KEY}.placeholders.number`) : undefined
                }
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="shippingUnit" label={t(`${KEY}.labels.unit`)}>
              <Input
                maxLength={40}
                placeholder={
                  editing ? t(`${KEY}.placeholders.unit`) : undefined
                }
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="shippingRegionId"
              label={regionLabel}
              rules={[required('region')]}
            >
              <Select
                showSearch
                allowClear
                loading={citiesLoading}
                disabled={fieldsDisabled || citiesLoading}
                options={regionOptions}
                optionFilterProp="label"
                placeholder={t(`${BASIC}.placeholders.region`)}
                onChange={() => form.setFieldValue('shippingCityId', undefined)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="shippingCityId"
              label={cityLabel}
              rules={[required('city')]}
            >
              <Select
                showSearch
                allowClear
                loading={citiesLoading}
                disabled={fieldsDisabled || citiesLoading || !regionId}
                options={cityOptions}
                optionFilterProp="label"
                placeholder={
                  regionId
                    ? t(`${BASIC}.placeholders.city`)
                    : t(
                        'scenes.leads.scene.filters.city_placeholder_no_region',
                        {
                          region: regionLabel,
                        }
                      )
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {editing && (
          <SectionEditFooter
            saving={saving}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        )}
      </Form>
    </Card>
  );
}

export default PaymentShippingSection;
